import http from './http'

const DailyFoodJournalService = {
  
  // Get list of journals with optional filters
  getJournals(params = {}) {
    return http.get('/daily_food_journals', { params })
  },
  
  // Get journal for specific date (YYYY-MM-DD format)
  getJournalByDate(date) {
    return http.get(`/daily_food_journals/${date}`)
  },
  
  // Get today's journal
  getTodaysJournal() {
    const today = new Date().toISOString().split('T')[0]
    return this.getJournalByDate(today)
  },
  
  // Create or update a journal for a specific date
  createOrUpdateJournal(date, journalData = {}) {
    return http.post('/daily_food_journals', {
      daily_food_journal: {
        date,
        ...journalData
      }
    })
  },
  
  // Add a food entry to a journal
  addFoodEntry(date, entryData) {
    return http.post('/daily_food_journals', {
      daily_food_journal: { date },
      journal_entry: entryData
    })
  },
  
  // Update an existing food entry
  updateFoodEntry(date, entryId, entryData) {
    return http.put(`/daily_food_journals/${date}`, {
      entry_id: entryId,
      journal_entry: entryData
    })
  },
  
  // Delete a food entry
  deleteFoodEntry(date, entryId) {
    return http.delete(`/daily_food_journals/${date}/entries/${entryId}`)
  },
  
  // Copy entries from another date
  copyFromDate(targetDate, sourceDate) {
    return http.post('/daily_food_journals', {
      daily_food_journal: { date: targetDate },
      copy_from_date: sourceDate
    })
  },

  // Update water intake for a specific date
  updateWaterIntake(date, waterIntake) {
    return http.put(`/daily_food_journals/${date}`, {
      daily_food_journal: {
        water_intake: waterIntake
      }
    })
  },

  // Add water to existing intake (atomic increment)
  addWater(date, ounces) {
    return http.post(`/daily_food_journals/${date}/increment_water`, {
      amount: ounces
    })
  },
  
  // Get user stats for a date range
  getStats(params = {}) {
    return http.get('/daily_food_journals/stats', { params })
  },

  // Get earliest journal date for a user
  getEarliestJournalDate(params = {}) {
    return http.get('/daily_food_journals/earliest', { params })
  },
  
  // Get week of journals (7 days ending on specified date)
  getWeekJournals(endDate = null) {
    const end = endDate || this.formatDate(new Date())
    // Parse the date string as local date
    const [year, month, day] = end.split('-').map(Number)
    const startDate = new Date(year, month - 1, day)
    startDate.setDate(startDate.getDate() - 6)
    const start = this.formatDate(startDate)

    return this.getJournals({
      start_date: start,
      end_date: end
    })
  },

  // Helper: Format date for API calls (local time, not UTC)
  formatDate(date) {
    if (typeof date === 'string') return date
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  },
  
  // Helper: Get meal type options
  getMealTypes() {
    return [
      { value: 'breakfast', label: 'Breakfast' },
      { value: 'morning_snack', label: 'Morning Snack' },
      { value: 'lunch', label: 'Lunch' },
      { value: 'afternoon_snack', label: 'Afternoon Snack' },
      { value: 'dinner', label: 'Dinner' },
      { value: 'evening_snack', label: 'Evening Snack' }
    ]
  },
  
  // Helper: Calculate macro percentages
  calculateMacroPercentages(protein, carbs, fat) {
    const proteinCals = protein * 4
    const carbCals = carbs * 4
    const fatCals = fat * 9
    const totalCals = proteinCals + carbCals + fatCals
    
    if (totalCals === 0) {
      return { protein: 0, carbs: 0, fat: 0 }
    }
    
    return {
      protein: Math.round((proteinCals / totalCals) * 100),
      carbs: Math.round((carbCals / totalCals) * 100),
      fat: Math.round((fatCals / totalCals) * 100)
    }
  },
  
  // Helper: Validate food entry data
  validateFoodEntry(entryData) {
    const errors = {}
    
    if (!entryData.food_name || entryData.food_name.trim() === '') {
      errors.food_name = 'Food name is required'
    }
    
    if (!entryData.portion || entryData.portion.trim() === '') {
      errors.portion = 'Portion is required'
    }
    
    if (!entryData.meal_type) {
      errors.meal_type = 'Meal type is required'
    }
    
    const validMealTypes = this.getMealTypes().map(m => m.value)
    if (entryData.meal_type && !validMealTypes.includes(entryData.meal_type)) {
      errors.meal_type = 'Invalid meal type'
    }
    
    // Validate numeric fields
    const numericFields = ['calories', 'protein', 'carbs', 'fat', 'fiber', 'sugar', 'sodium']
    numericFields.forEach(field => {
      if (entryData[field] !== undefined && entryData[field] !== null) {
        const value = Number(entryData[field])
        if (isNaN(value) || value < 0) {
          errors[field] = `${field} must be a non-negative number`
        }
      }
    })
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
}

export default DailyFoodJournalService